import React, { useEffect, useMemo } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useGetPositionInterviewsQuery } from "../redux/api/interview.api";
import Pagination from "../components/interview/pagination";
import { GoogleInFeedAd } from "../components/ads/GoogleInFeedAd";
import { useNavigate } from "react-router-dom";
import { SessionStorage } from "../types/general.type";

export default function PositionInterviews() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = React.useState(+(searchParams.get('pageIndex') || '0'));
  const [start, setStart] = React.useState(0);
  const [end, setEnd] = React.useState(5);
  const jobTitle = searchParams.get('jobTitle') || '';
  const seniority = searchParams.get('seniority') || '';
  const params = useParams();
  const positionId = +(params?.positionId || 0)
  const { data: interviewsData, isLoading } = useGetPositionInterviewsQuery({ positionId }, {
    skip: !positionId,
    refetchOnMountOrArgChange: true,
  });



  const interviews = useMemo(() => interviewsData?.interviews.map((interview) => ({
    id: interview.interviewId,
    interviewer: interview.interviewer || "N/A",
    duration: interview.duration,
    date: new Date(interview.createdDate).toDateString(),
    status: interview?.status || "pending",
    overallScore: interview.overallScore,
    overallReport: interview.overallReport || "N/A",

  })) || [], [interviewsData]);


  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('pageIndex', page.toString());
    setSearchParams(params);
  }, [page, searchParams, setSearchParams]);

  useEffect(() => {
    setStart((page) * 5);
    setEnd((page + 1) * 5);
  }, [page]);


  const paginatedInterviews = useMemo(() => interviews.slice(start, end), [interviews, start, end]);
  const totalPages = Math.ceil(interviews.length / 5);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen w-full   overflow-auto bg-gray-50 dark:bg-[#1a1a1a] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="relative w-16 h-16">
            <div className="absolute top-0 left-0 w-full h-full border-4 border-teal-200 dark:border-teal-900 rounded-full opacity-25"></div>
            <div className="absolute top-0 left-0 w-full h-full border-4 border-t-teal-500 rounded-full animate-spin"></div>
          </div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium">Loading interview data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`w-full mobile:h-[85vh] `}>
      <div className="bg-gray-100 dark:bg-[#1a1a1a]/80 text-gray-900 dark:text-gray-100 transition-colors  h-full p-4">
        <header className="bg-white mb-2 dark:bg-[#272727] shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link
                to="/interviews"
                className="inline-flex items-center text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Positions
              </Link>
            </div>
          </div>
        </header>

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl md:text-2xl font-bold">
            List of Interviews for {jobTitle}
          </h1>
        </div>

        {/* Interview Cards */}
        <div className="space-y-4 custom-scrollbar overflow-y-auto !h-auto !max-h-[60vh] mb-4">
          {paginatedInterviews.map((interview, index) => {
            const overallScore = interview.overallScore ? interview.overallScore.includes('%') ? +interview.overallScore.replace('%', '') : +interview.overallScore : 0
            return (
              <>
                <div
                  key={interview.id}
                  className="flex flex-col small:flex-row small:items-center justify-between p-4 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#212121] shadow-sm"
                >
                  {/* Info */}
                  <div className="flex small:flex-[1.75] flex-row justify-between  small:items-center gap-4 mb-4 small:mb-0">
                    <div className="space-y-1">
                      <p className="text-sm lg:text-base">
                        <span className="font-semibold">Interviewer:</span> {interview.interviewer}
                      </p>
                      <p>
                        <span className="font-semibold">Duration:</span> {interview.duration || "N/A"} {interview.duration && 'min'}
                      </p>
                      <p className="text-sm lg:text-base">
                        <span className="font-semibold">Overall Score:</span> {overallScore}%
                        <div className=" w-full mobile:w-[10rem] md:w-[15rem] tablet:w-[13rem] lg:w-[20rem] bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                          {

                            <div
                              className={`h-2.5 rounded-full ${+overallScore >= 90
                                ? "bg-green-500"
                                : +overallScore >= 70
                                  ? "bg-blue-500"
                                  : +overallScore >= 50
                                    ? "bg-amber-500"
                                    : "bg-red-500"
                                }`}
                              style={{ width: `${+overallScore}%` }}
                            ></div>
                          }
                        </div>
                      </p>
                    </div>
                    <div className="flex-col flex-1 w-fit text-sm lg:text-base flex items-center gap-2">
                      {
                        interview.status === 'InterviewCompeleted'
                          ?
                          <span
                            className="px-5 py-0.5 rounded-full text-xs font-semibold bg-accent-500 text-white"
                          >
                            Completed
                          </span>
                          :
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-500 text-white"
                          >
                            Canceled
                          </span>
                      }
                      <span className="text-sm text-nowrap lg:text-base">
                        <span className="font-semibold ">Date:</span> {interview.date}
                      </span>
                    </div>
                  </div>

                  {/* Score Button */}
                  <div className="small:flex-1  flex justify-stretch small:justify-end items-center gap-2">

                    {
                      interview.status === 'InterviewCompeleted' ?
                        (
                          <button
                          onClick={() => {
                            const overallReport = interview?.overallReport || "N/A"
                            sessionStorage.setItem(SessionStorage.OVERALL_REPORT, overallReport)
                            navigate(`/interview-scores/${interview.id}?jobTitle=${jobTitle}&seniority=${seniority}`);
        
                          }}
                          className="inline-block w-full small:w-auto text-center  text-sm px-4 py-2 bg-[#3FBBA3] text-white font-medium rounded-lg hover:bg-[#36a08c] transition-colors shadow-sm"
                        >
                          Detail Report
                        </button>
                          // <Link
                          //   to={`/interview-scores/${interview.id}?jobTitle=${jobTitle}&seniority=${seniority}`}
                          //   className="inline-block w-full small:w-auto text-center  text-sm px-4 py-2 bg-[#3FBBA3] text-white font-medium rounded-lg hover:bg-[#36a08c] transition-colors shadow-sm"
                          // >
                          //   Detail Report
                          // </Link>
                        ) :
                        (
                          <button
                            disabled
                            className="inline-block w-full small:w-auto text-sm px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium rounded-lg cursor-not-allowed"
                          >
                            Scores Unavailable
                          </button>
                        )
                    }
                  </div>
                </div>
                {((index + 1) % 5 == 0 || index == 0) && (
                  <GoogleInFeedAd />
                )}
              </>
            )
          })}
        </div>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(value) => setPage(value)}
          perPage={5}
          totalItems={interviews.length}
        />

        {/* Pagination */}
        {/* {

          <div className="flex items-center justify-center mt-6 gap-2">
            <button
              className="flex bg-accent-500 py-2 items-center disabled:opacity-80 disabled:cursor-not-allowed gap-1 px-2 text-white rounded-md text-sm hover:bg-accent-600 disabled:hover:bg-accent-300 "
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index + 1}
                className={`px-3 py-1.5  border-accent-300 rounded-md text-sm ${index + 1 === page
                  ? "bg-accent-500 text-white border-accent-500"
                  : "hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            ))}
            <button
              className="flex bg-accent-500 text-white items-center disabled:opacity-80 disabled:cursor-not-allowed gap-1 px-2 py-2  rounded-md text-sm hover:bg-accent-600 disabled:hover:bg-accent-300 "
              onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
              <ChevronRight size={14} />
            </button>
          </div>} */}
      </div>
    </div>
  );
}
